/**
 * Signing in, as far as the browser is concerned.
 *
 * The sign-in itself does not happen here. Azure Static Web Apps performs it
 * against Entra External ID, and the browser simply leaves for Microsoft and
 * comes back. No password, and no token, ever passes through this code — which
 * is why the welcome screen can promise exactly that.
 *
 * What this file does is answer one question without side effects: is anyone
 * signed in? `/.auth/me` is a built-in endpoint that answers it with a plain
 * JSON `null` for a visitor, instead of bouncing them to a sign-in page. That
 * distinction is the whole reason a welcome screen can exist at all.
 */

const PROVIDER = 'entraexternalid';

/** Where a completed sign-out lands, so the reader gets a real confirmation. */
export const SIGNED_OUT_PATH = '/signed-out';

/** Where a sign-in that did not complete lands. */
export const SIGN_IN_FAILED_PATH = '/sign-in-failed';

export interface Principal {
  userId: string;
  userDetails: string | null;
  identityProvider: string;
}

interface AuthMeResponse {
  clientPrincipal: {
    userId?: string;
    userDetails?: string;
    identityProvider?: string;
  } | null;
}

/** The signed-in person, or null for a visitor. Never throws. */
export async function loadPrincipal(): Promise<Principal | null> {
  try {
    const response = await fetch('/.auth/me', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;

    const body = (await response.json()) as AuthMeResponse;
    const principal = body.clientPrincipal;
    if (!principal?.userId) return null;

    return {
      userId: principal.userId,
      userDetails: principal.userDetails ?? null,
      identityProvider: principal.identityProvider ?? PROVIDER,
    };
  } catch {
    // Offline, or the platform is not answering. Treated as "not signed in",
    // which shows the welcome screen — the safe direction to fail in.
    return null;
  }
}

export function signOutUrl(): string {
  return `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(SIGNED_OUT_PATH)}`;
}

/**
 * A sign-in that was started but never finished leaves no trace anywhere:
 * Microsoft simply returns the visitor to the site, still a visitor. Without a
 * marker we cannot tell that from someone arriving for the first time, and the
 * difference matters — one needs a welcome, the other needs to be told what
 * went wrong.
 *
 * The marker lives in session storage, so it dies with the browser tab, and it
 * carries a timestamp so a tab left open overnight does not greet its owner
 * with a stale failure.
 */
const ATTEMPT_KEY = 'cfoai.signin-attempt';
const ATTEMPT_WINDOW_MS = 10 * 60 * 1000;

export function beginSignIn(returnTo = '/'): void {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
  } catch {
    // Without the marker a failed sign-in shows the welcome screen again.
    // Less helpful, but not wrong, so it is not worth blocking sign-in.
  }
  const target = `/.auth/login/${PROVIDER}?post_login_redirect_uri=${encodeURIComponent(returnTo)}`;
  window.location.assign(target);
}

export function signInWasAttempted(): boolean {
  try {
    const raw = window.sessionStorage.getItem(ATTEMPT_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < ATTEMPT_WINDOW_MS;
  } catch {
    return false;
  }
}

export function clearSignInAttempt(): void {
  try {
    window.sessionStorage.removeItem(ATTEMPT_KEY);
  } catch {
    // Nothing to clear if nothing could be stored.
  }
}
